// Serialize a TipTap/ProseMirror document (a text content window, TODO §2) to a static,
// self-contained HTML string for the HTML export. The live editor renders these docs with
// TipTap; the exported file has no React/TipTap, so this reproduces the same nodes/marks as
// plain HTML. Only the node/mark set StarterKit + TextAlign can produce is handled
// (paragraph, heading, bullet/ordered lists, hardBreak; bold/italic/underline/strike/code +
// textStyle). Everything is HTML-escaped — deck content is user-authored and the exported
// file may be shared, so this is an XSS boundary.

// Escape text for HTML body context.
export function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Escape a value going into a double-quoted attribute (e.g. an inline style).
function escapeAttr(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
}

interface PMMark {
  type: string;
  attrs?: Record<string, unknown>;
}
interface PMNode {
  type?: string;
  text?: string;
  attrs?: Record<string, unknown>;
  marks?: PMMark[];
  content?: PMNode[];
}

// A textStyle mark carries inline color/font overrides (same shape Gridd cells use). Turn
// the ones we know into a CSS declaration string.
function textStyleCss(attrs: Record<string, unknown> | undefined): string {
  if (!attrs) return "";
  const parts: string[] = [];
  const color = attrs.color;
  const fontFamily = attrs.fontFamily;
  const fontSize = attrs.fontSize;
  const backgroundColor = attrs.backgroundColor;
  if (typeof color === "string") parts.push(`color:${color}`);
  if (typeof fontFamily === "string") parts.push(`font-family:${fontFamily}`);
  if (typeof fontSize === "string") parts.push(`font-size:${fontSize}`);
  if (typeof backgroundColor === "string")
    parts.push(`background-color:${backgroundColor}`);
  return parts.join(";");
}

// Wrap already-escaped inner HTML in the tags a text node's marks call for. textStyle is
// applied innermost (as a span), then the boolean formatting marks around it.
function applyMarks(inner: string, marks: PMMark[] | undefined): string {
  if (!marks || marks.length === 0) return inner;
  let html = inner;
  const style = textStyleCss(marks.find((m) => m.type === "textStyle")?.attrs);
  if (style) html = `<span style="${escapeAttr(style)}">${html}</span>`;
  for (const mark of marks) {
    switch (mark.type) {
      case "bold":
        html = `<strong>${html}</strong>`;
        break;
      case "italic":
        html = `<em>${html}</em>`;
        break;
      case "underline":
        html = `<u>${html}</u>`;
        break;
      case "strike":
        html = `<s>${html}</s>`;
        break;
      case "code":
        html = `<code>${html}</code>`;
        break;
      // textStyle handled above; anything else is dropped (unknown mark → plain text).
    }
  }
  return html;
}

function renderInline(nodes: PMNode[] | undefined): string {
  if (!nodes) return "";
  return nodes
    .map((n) => {
      if (n.type === "text")
        return applyMarks(escapeHtml(n.text ?? ""), n.marks);
      if (n.type === "hardBreak") return "<br>";
      // A nested unknown inline node — fall back to its own text content.
      return renderInline(n.content);
    })
    .join("");
}

function alignStyle(attrs: Record<string, unknown> | undefined): string {
  const align = attrs?.textAlign;
  return typeof align === "string" && align
    ? ` style="text-align:${escapeAttr(align)}"`
    : "";
}

function renderBlock(node: PMNode): string {
  switch (node.type) {
    case "paragraph":
      return `<p${alignStyle(node.attrs)}>${renderInline(node.content)}</p>`;
    case "heading": {
      const level = Math.min(Math.max(Number(node.attrs?.level) || 1, 1), 6);
      return `<h${level}${alignStyle(node.attrs)}>${renderInline(node.content)}</h${level}>`;
    }
    case "bulletList":
      return `<ul>${(node.content ?? []).map(renderBlock).join("")}</ul>`;
    case "orderedList":
      return `<ol>${(node.content ?? []).map(renderBlock).join("")}</ol>`;
    case "listItem":
      return `<li>${(node.content ?? []).map(renderBlock).join("")}</li>`;
    case "blockquote":
      return `<blockquote>${(node.content ?? []).map(renderBlock).join("")}</blockquote>`;
    case "text":
    case "hardBreak":
      // A bare inline node at block level — wrap it so it still renders.
      return `<p>${renderInline([node])}</p>`;
    default:
      // Unknown block: render its children so content is never silently lost.
      return (node.content ?? []).map(renderBlock).join("");
  }
}

// Serialize a ProseMirror `doc` node to an HTML fragment. A falsy/!doc input yields "".
export function proseMirrorToHtml(doc: unknown): string {
  const root = doc as PMNode | null | undefined;
  if (!root || root.type !== "doc" || !root.content) return "";
  return root.content.map(renderBlock).join("");
}
